import styled from "styled-components";
import ScrollAnimation from "react-animate-on-scroll";
import React, { FunctionComponent } from "react";

import { getRetailMatchIndexColor } from "../../../Util/ColorUtil";
import { ReportH3 } from "../ReportStyles/Text";
import { getRetailMatchMetricVerbiage } from "../../../Util/RetailMatch/RetailMatchUtil";
import { RetailMatchMetricLevel } from "../../../Enums/RetailMatchMetricLevel";
import { device, sizeConstants } from "../../../globalStyles";
import { firstLetterCapitalizer } from "../../Common";
import { BreakoutItemVerbiage } from "../../Apps/RetailMatch/Styled/RetailMatchIntroStyles";

interface IIndexBarProps {
  index?: number;
  barTotal?: number;
  title?: string;
  subTitle?: string;
  scaleText?: RetailMatchMetricLevel;
  inSidebar?: boolean;
  metric?: RetailMatchMetricLevel;
  pillValue?: number;
}

export const IndexSubtitle = styled("span")`
  font-size: 17px;
  color: #000000;
  line-height: 22px;
`;

const IndexBarValue = styled.div`
  position: absolute;
  width: 0;
  height: 8px;
  border-radius: 4px;
  background-color: ${(props: IIndexBarProps) =>
        getRetailMatchIndexColor(props.index)};
  transition: width 0.4s;
  max-width: 100%;
`;

const IndexBarValueSharing = styled.div`
  position: absolute;
  width: ${(props: IIndexBarProps) => (props.index / props.barTotal) * 100}%;
  height: 8px;
  border-radius: 4px;
  background-color: ${(props: IIndexBarProps) =>
        getRetailMatchIndexColor(props.index)};
  max-width: 100%;
`;

const IndexPill = styled(ScrollAnimation)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  overflow: visible;
  text-align: center;
  color: #fff;
  border-radius: 64px;
  height: 0;
  width: 0;
  background-color: transparent;
  margin: 0 auto;
  transition: background-color 0.3s, width 0.3s, height 0.3s;

  &.active {
    width: 80px;
    height: 80px;
    background-color: ${(props: IIndexBarProps) =>
        getRetailMatchIndexColor(props.index)};
  }

  ${device.desktop} {
    &.active {
      width: 129px;
      height: 129px;
    }
  }
`;

const IndexPillSharing = styled.div`
  position: absolute;
  left: ${(props: IIndexBarProps) => props.pillValue}%;
  top: -11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  color: #fff;
  border-radius: 64px;
  margin: 0 auto;
  width: ${(props: IIndexBarProps) => (props.inSidebar ? "25px" : "30px")};
  height: ${(props: IIndexBarProps) => (props.inSidebar ? "25px" : "30px")};
  background-color: ${(props: IIndexBarProps) =>
        getRetailMatchIndexColor(props.index)};
  z-index: 1;
`;

const IndexBarWrapper = styled(ScrollAnimation)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: visible;
  height: 12px;
  grid-column-start: span 2;
  width: calc(
    100% - 40px
  ); /* Creates padding without having to apply it to all children */
  margin: 0 auto;
  &.active {
    > ${IndexBarValue} {
      width: ${(props: IIndexBarProps) =>
        (props.index / props.barTotal) * 100}%;
    }
  }

  ${device.desktop} {
    grid-column-start: 2;
  }
`;

const IndexBarWrapperSharing = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 12px;
  width: calc(
    100% - 40px
  ); /* Creates padding without having to apply it to all children */
  margin-bottom: ${(props: IIndexBarProps) => props.inSidebar && "25px"};
`;

const IndexBarTotal = styled.div`
  position: absolute;
  display: flex;
  width: 100%;
  max-width: 100%;
  height: 8px;
  border-radius: 4px;
  background-color: #f5f6f7;
`;

const IndexBarScale = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  color: rgba(51, 51, 51, 0.6);
  font-weight: 900;
  width: 100%;
  max-width: 100%;
  margin-top: 8px;
`;

const IndexBarScaleValueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 14px;
  width: 2px;
`;

const IndexBarScaleValueTick = styled.div`
  width: 2px;
  height: 10px;
  border-radius: 2px;
  background-color: #969ea6;
  margin-bottom: 5px;
`;

const IndexBarInfoText = styled.div`
  width: 100%;
`;

const IndexTextValue = styled.div`
  width: 58px;
  height: 16px;
  font-size: 16px;
  font-weight: 900;
  ${device.desktop} {
    font-size: 19px;
    height: 19px;
  }
`;

const IndexText = styled.div`
  width: 52px;
  height: 8px;
  font-size: 8px;
  font-weight: 500;
  ${device.desktop} {
    font-size: 11px;
    height: 11px;
  }
`;

export const IndexBar: FunctionComponent<IIndexBarProps> = (
    props: IIndexBarProps
) => {
    const { index, scaleText } = props;
    const maxIndex = 200;
    const scaleValues = [0, 100, maxIndex];
    const barIndex = index;

    const getIndexPillSubtitle = (val: number): string => {
        const text: string = getRetailMatchMetricVerbiage(val, scaleText);

        return firstLetterCapitalizer(text);
    };

    return (
        <>
            <IndexPill
                index={index}
                animateIn="active"
                scrollableParentSelector="#RetailMatchReportBody"
                initiallyVisible={true}
                offset={100}
                animateOnce={true}
            >
                <IndexTextValue>{index}</IndexTextValue>
                <IndexText>INDEX</IndexText>
            </IndexPill>
            <IndexBarInfoText>
                <ReportH3 style={{ margin: 0 }}>{props.title}</ReportH3>
                <IndexSubtitle>{props.subTitle}</IndexSubtitle>
            </IndexBarInfoText>
            <IndexBarWrapper
                animateIn="active"
                scrollableParentSelector="#RetailMatchReportBody"
                initiallyVisible={true}
                index={barIndex}
                barTotal={maxIndex}
                offset={20}
                animateOnce={true}
            >
                <IndexBarTotal />
                <IndexBarValue index={index} />
                <IndexBarScale>
                    {scaleValues.map((val: number, i: number) => (
                        <IndexBarScaleValueWrapper key={i}>
                            <IndexBarScaleValueTick />
                            <div>{val}</div>
                            <div
                                style={{
                                    width: "90px",
                                    textAlign: "center",
                                    marginTop: "5px",
                                }}
                            >
                                {getIndexPillSubtitle(val)}
                            </div>
                        </IndexBarScaleValueWrapper>
                    ))}
                </IndexBarScale>
            </IndexBarWrapper>
        </>
    );
};

export const IndexBarSharing: FunctionComponent<IIndexBarProps> = ({
    index,
    scaleText,
    metric,
    inSidebar,
    title,
    subTitle,
}: IIndexBarProps) => {
    const maxIndex = 200;
    const scaleValues = [0, 100, maxIndex];
    let pillValue: number = (index / maxIndex) * 100 - 1;

    if (pillValue > 100) {
        pillValue = 99;
    }

    const getIndexPillSubtitle = (val: number): string => {
        const text: string = getRetailMatchMetricVerbiage(val, scaleText);

        return firstLetterCapitalizer(text);
    };

    return (
        <>
            <IndexBarInfoText
                style={{
                    display: "flex",
                    flexDirection: inSidebar ? "column" : "initial",
                    alignItems: inSidebar ? "initial" : "center",
                    marginBottom: !inSidebar ? 10 : 13,
                    height: inSidebar && "51px",
                }}
            >
                <div style={{ display: "flex", alignItems: "center" }}>
                    <ReportH3
                        style={{
                            margin: 0,
                            fontSize: "13px",
                            lineHeight: inSidebar ? "normal" : "",
                        }}
                    >
                        {title}
                    </ReportH3>
                    {inSidebar && (
                        <>
                            <b style={{ margin: "0 3px 0 3px" }}> - </b>
                            <BreakoutItemVerbiage
                                value={index}
                                style={{ fontSize: 15, margin: 0, whiteSpace: "nowrap" }}
                            >
                                <span>
                                    <b style={{ color: getRetailMatchIndexColor(index) }}>
                                        {getRetailMatchMetricVerbiage(index, scaleText)}
                                    </b>
                                </span>
                            </BreakoutItemVerbiage>
                        </>
                    )}
                </div>
                <IndexSubtitle
                    style={{
                        fontSize: inSidebar ? "10px" : "13px",
                        lineHeight: inSidebar && "normal",
                    }}
                >
                    {!inSidebar && " - "}
                    {subTitle}
                </IndexSubtitle>
            </IndexBarInfoText>
            <IndexBarWrapperSharing inSidebar={inSidebar}>
                <IndexPillSharing
                    index={index}
                    barTotal={maxIndex}
                    pillValue={pillValue}
                >
                    <IndexTextValue style={{ fontSize: "13px", height: "initial" }}>
                        {index}
                    </IndexTextValue>
                </IndexPillSharing>
                <IndexBarTotal />
                <IndexBarValueSharing index={index} barTotal={maxIndex} />
                {!inSidebar && (
                    <IndexBarScale>
                        {scaleValues.map((val: number, i: number) => (
                            <IndexBarScaleValueWrapper style={{ fontSize: "10px" }} key={i}>
                                <IndexBarScaleValueTick style={{ width: "1px" }} />
                                <div>{val}</div>
                                <div style={{ width: "90px", textAlign: "center" }}>
                                    {getIndexPillSubtitle(val)}
                                </div>
                            </IndexBarScaleValueWrapper>
                        ))}
                    </IndexBarScale>
                )}
            </IndexBarWrapperSharing>
        </>
    );
};
